// crewbit-admin/src/components/money/InvoicesTableRow.tsx
//
// Single <tr> for the admin /money/invoices table. 7 columns:
// Invoice # | Client | Total | Status | Created | Due | Actions(kebab).
//
// Row click opens MoneyInvoiceDetailPanel (parent owns ?invoice_id= URL).
// Kebab menu surfaces 4 contextual actions — mark paid / send / refund /
// cancel — each routed to parent-mounted modals via onActionClick.
//
// SPEC: §Money.InvoicesListAdmin §3 (D1, D5).
// Refund kebab limitation v1: alreadyRefunded passed as 0 since useInvoices
// SELECT doesn't return refund_amount. Substrate enforces refund cap; UX
// fallback is to open detail panel for partial-refund history. Filed at
// Phase E close-out as §Admin.InvoicesListRefundContext sub-finding if
// operator surfaces the gap.

import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import type { InvoiceRow } from '../../types';

export type InvoiceRowAction = 'mark_paid' | 'send' | 'refund' | 'cancel';

interface InvoicesTableRowProps {
  invoice: InvoiceRow;
  onRowClick: (invoiceId: string) => void;
  onActionClick: (invoice: InvoiceRow, action: InvoiceRowAction) => void;
  /** §Money.OverviewBulkOperations: when defined, render checkbox cell. */
  selected?: boolean;
  onToggleSelect?: (invoiceId: string) => void;
}

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13,
  color: palette.g700,
  borderBottom: `1px solid ${palette.g100}`,
  verticalAlign: 'middle',
};

interface StatusTreatment {
  bg: string;
  fg: string;
  label: string;
}

function statusTreatment(status: string): StatusTreatment {
  switch (status) {
    case 'paid':
      return { bg: palette.greenWash, fg: palette.green, label: 'Paid' };
    case 'overdue':
      return { bg: palette.redWash, fg: palette.red, label: 'Overdue' };
    case 'sent':
      return { bg: palette.blueWash, fg: palette.blue, label: 'Sent' };
    case 'viewed':
      return { bg: palette.blueWash, fg: palette.blue, label: 'Viewed' };
    case 'draft':
      return { bg: palette.g100, fg: palette.g600, label: 'Draft' };
    case 'cancelled':
      return { bg: palette.g100, fg: palette.g500, label: 'Cancelled' };
    case 'written_off':
      return { bg: palette.g100, fg: palette.g500, label: 'Written off' };
    case 'partially_refunded':
      return { bg: palette.amberWash, fg: palette.amber, label: 'Partially refunded' };
    case 'refunded':
      return { bg: palette.amberWash, fg: palette.amber, label: 'Refunded' };
    case 'voided':
      return { bg: palette.g100, fg: palette.g500, label: 'Voided' };
    default:
      return { bg: palette.g100, fg: palette.g500, label: status };
  }
}

function StatusPill({ status }: { status: string }) {
  const t = statusTreatment(status);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11.5,
        fontWeight: typography.weight.medium,
        background: t.bg,
        color: t.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {t.label}
    </span>
  );
}

function formatAbsoluteDate(input: string | null): string {
  if (!input) return '';
  const dateOnly = input.length > 10 ? input.slice(0, 10) : input;
  const d = new Date(`${dateOnly}T00:00:00`);
  if (isNaN(d.getTime())) return input;
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelativeDate(input: string | null, nowMs: number): string {
  if (!input) return '—';
  const dateOnly = input.length > 10 ? input.slice(0, 10) : input;
  const target = new Date(`${dateOnly}T12:00:00`).getTime();
  if (isNaN(target)) return input;
  const days = Math.round((target - nowMs) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 0) return `In ${days}d`;
  return `${Math.abs(days)}d ago`;
}

interface ActionAvailability {
  canMarkPaid: boolean;
  canSend: boolean;
  canRefund: boolean;
  canCancel: boolean;
}

function deriveActionAvailability(status: string): ActionAvailability {
  const terminal = status === 'cancelled' || status === 'written_off' || status === 'voided';
  const paidFamily = status === 'paid' || status === 'refunded' || status === 'partially_refunded';
  return {
    canMarkPaid: !terminal && !paidFamily,
    canSend: status === 'draft' || status === 'sent' || status === 'viewed' || status === 'overdue',
    canRefund: status === 'paid' || status === 'partially_refunded',
    canCancel: !terminal && status !== 'paid' && status !== 'refunded',
  };
}

export function InvoicesTableRow({ invoice, onRowClick, onActionClick, selected, onToggleSelect }: InvoicesTableRowProps) {
  const selectionEnabled = selected !== undefined && !!onToggleSelect;
  const [nowMs] = useState(() => Date.now());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const availability = deriveActionAvailability(invoice.status);
  const anyAction = availability.canMarkPaid || availability.canSend || availability.canRefund || availability.canCancel;

  const stopAndAct = (action: InvoiceRowAction) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    onActionClick(invoice, action);
  };

  const overdueTone = invoice.status === 'overdue' && invoice.due_date;

  return (
    <tr
      onClick={() => onRowClick(invoice.id)}
      style={{
        cursor: 'pointer',
        transition: `background ${transition.fast}`,
        background: selected ? palette.blueWash : undefined,
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = palette.g50;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = selected ? palette.blueWash : '';
      }}
    >
      {selectionEnabled && (
        <td style={{ ...tdStyle, width: 36, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Select invoice ${invoice.invoice_number}`}
            checked={selected}
            onChange={() => onToggleSelect!(invoice.id)}
          />
        </td>
      )}
      <td style={{ ...tdStyle, fontWeight: typography.weight.semibold, color: palette.g800 }}>
        {invoice.invoice_number}
      </td>
      <td
        style={{
          ...tdStyle,
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={invoice.client_name ?? ''}
      >
        {invoice.client_name ?? '—'}
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontWeight: typography.weight.semibold,
          fontVariantNumeric: 'tabular-nums',
          color: palette.g800,
        }}
      >
        {formatMoney(invoice.total)}
      </td>
      <td style={tdStyle}>
        <StatusPill status={invoice.status} />
      </td>
      <td style={{ ...tdStyle, color: palette.g500 }} title={formatAbsoluteDate(invoice.created_at)}>
        {formatRelativeDate(invoice.created_at, nowMs)}
      </td>
      <td
        style={{
          ...tdStyle,
          color: overdueTone ? palette.red : palette.g500,
          fontWeight: overdueTone ? typography.weight.semibold : typography.weight.regular,
        }}
        title={formatAbsoluteDate(invoice.due_date)}
      >
        {formatRelativeDate(invoice.due_date, nowMs)}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {anyAction && (
          <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              aria-label="Row actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 6,
                border: `1px solid transparent`,
                background: menuOpen ? palette.g100 : 'transparent',
                color: palette.g600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: `background ${transition.fast}`,
              }}
              onMouseEnter={(e) => {
                if (!menuOpen) e.currentTarget.style.background = palette.g100;
              }}
              onMouseLeave={(e) => {
                if (!menuOpen) e.currentTarget.style.background = 'transparent';
              }}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  background: palette.white,
                  border: `1px solid ${palette.g200}`,
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  padding: spacing.xs,
                  minWidth: 160,
                  zIndex: 60,
                  textAlign: 'left',
                }}
              >
                {availability.canMarkPaid && (
                  <MenuItem label="Mark paid" onClick={stopAndAct('mark_paid')} />
                )}
                {availability.canSend && (
                  <MenuItem label="Send" onClick={stopAndAct('send')} />
                )}
                {availability.canRefund && (
                  <MenuItem label="Refund" onClick={stopAndAct('refund')} />
                )}
                {availability.canCancel && (
                  <MenuItem label="Cancel" onClick={stopAndAct('cancel')} tone="danger" />
                )}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

interface MenuItemProps {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  tone?: 'default' | 'danger';
}

function MenuItem({ label, onClick, tone = 'default' }: MenuItemProps) {
  const color = tone === 'danger' ? palette.red : palette.g700;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        borderRadius: 8,
        border: 'none',
        background: 'transparent',
        color,
        fontSize: typography.size.caption,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = palette.g50)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {label}
    </button>
  );
}
